import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useAuthStore } from '../../stores/authStore'
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  PencilIcon,
  CameraIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  GlobeAltIcon,
  BeakerIcon,
  LanguageIcon,
  TrophyIcon,
  SparklesIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  DocumentTextIcon,
  MapPinIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { CheckBadgeIcon, SparklesIcon as SparklesSolid } from '@heroicons/react/24/solid'
import { AvatarHelper } from '../../utils/avatarHelper'
import UserAvatar from '../../components/UserAvatar'
import { StudentPageLayout } from '../../components/student/StudentPageLayout'
import { StudentSection } from '../../components/student/StudentSection'

// 增强版能力雷达图组件
const AbilityRadar = ({ profileData }: { profileData?: StudentProfileData | null }) => {
  const size = 240
  const center = size / 2
  const radius = 90
  const axes = 6

  // 根据真实数据计算能力值
  const getAbilityValues = () => {
    if (!profileData) {
      return [
        { label: 'GPA', value: 0, icon: AcademicCapIcon, color: '#6366F1' },
        { label: '语言', value: 0, icon: LanguageIcon, color: '#8B5CF6' },
        { label: '科研', value: 0, icon: BeakerIcon, color: '#EC4899' },
        { label: '实习', value: 0, icon: BriefcaseIcon, color: '#F59E0B' },
        { label: '竞赛', value: 0, icon: TrophyIcon, color: '#10B981' },
        { label: '公益', value: 0, icon: GlobeAltIcon, color: '#3B82F6' }
      ]
    }

    // 从语言成绩计算语言能力
    const languageScores = profileData.languageScores || {}
    const languageValue = Object.values(languageScores).reduce((acc, score) => {
      if (score.targetScore > 0) {
        return Math.max(acc, Math.min(100, (score.currentScore / score.targetScore) * 100))
      }
      return acc
    }, 0)

    // 从进度计算整体完成度
    const progressValue = profileData.progress?.overall || 0

    return [
      { label: 'GPA', value: progressValue > 0 ? Math.min(100, progressValue + 20) : 50, icon: AcademicCapIcon, color: '#6366F1' },
      { label: '语言', value: languageValue || 50, icon: LanguageIcon, color: '#8B5CF6' },
      { label: '科研', value: Math.min(100, (profileData.targets?.nextMilestones?.length || 0) * 20), icon: BeakerIcon, color: '#EC4899' },
      { label: '实习', value: profileData.preferences?.interests?.length ? 70 : 40, icon: BriefcaseIcon, color: '#F59E0B' },
      { label: '竞赛', value: progressValue > 50 ? 65 : 35, icon: TrophyIcon, color: '#10B981' },
      { label: '公益', value: profileData.preferences?.subjects?.length ? 75 : 45, icon: GlobeAltIcon, color: '#3B82F6' }
    ]
  }

  const abilities = getAbilityValues()

  const getPoint = (value: number, index: number) => {
    const angle = (Math.PI * 2 * index) / axes - Math.PI / 2
    const distance = (value / 100) * radius
    return {
      x: center + distance * Math.cos(angle),
      y: center + distance * Math.sin(angle)
    }
  }

  const polygonPoints = abilities
    .map((a, i) => {
      const p = getPoint(a.value, i)
      return `${p.x},${p.y}`
    })
    .join(' ')

  // 生成多层背景网格
  const gridLevels = [100, 75, 50, 25]

  return (
    <div className="relative flex justify-center py-6">
      <svg width={size} height={size} className="overflow-visible">
        <defs>
          <linearGradient id="radarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(99, 102, 241, 0.3)" />
            <stop offset="100%" stopColor="rgba(139, 92, 246, 0.3)" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* 背景网格 */}
        {gridLevels.map((level, idx) => (
          <polygon
            key={level}
            points={abilities
              .map((_, i) => {
                const p = getPoint(level, i)
                return `${p.x},${p.y}`
              })
              .join(' ')}
            fill="none"
            stroke={idx === 0 ? '#E5E7EB' : '#F3F4F6'}
            strokeWidth={idx === 0 ? 1.5 : 1}
            strokeDasharray={idx > 0 ? '4 4' : ''}
          />
        ))}

        {/* 轴线 */}
        {abilities.map((_, i) => {
          const p = getPoint(100, i)
          return (
            <line
              key={i}
              x1={center}
              y1={center}
              x2={p.x}
              y2={p.y}
              stroke="#E5E7EB"
              strokeWidth="1"
            />
          )
        })}

        {/* 数据区域 */}
        <polygon
          points={polygonPoints}
          fill="url(#radarGradient)"
          stroke="url(#radarGradient)"
          strokeWidth="3"
          filter="url(#glow)"
          className="transition-all duration-500"
        />
        <polygon
          points={polygonPoints}
          fill="none"
          stroke="#6366F1"
          strokeWidth="2"
          strokeLinejoin="round"
        />

        {/* 数据点 */}
        {abilities.map((a, i) => {
          const p = getPoint(a.value, i)
          return (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="6" fill="white" stroke="#6366F1" strokeWidth="2" />
              <circle cx={p.x} cy={p.y} r="3" fill="#6366F1" />
            </g>
          )
        })}
      </svg>

      {/* 标签 */}
      {abilities.map((a, i) => {
        const p = getPoint(120, i)
        return (
          <div
            key={i}
            className="absolute flex flex-col items-center"
            style={{
              left: `${p.x}px`,
              top: `${p.y}px`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center mb-1 shadow-sm"
              style={{ backgroundColor: `${a.color}15` }}
            >
              <a.icon className="w-4 h-4" style={{ color: a.color }} />
            </div>
            <span className="text-xs font-semibold text-slate-600">{a.label}</span>
            <span className="text-xs font-bold" style={{ color: a.color }}>
              {a.value}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// 申请漏斗组件 - 增强版
const ApplicationFunnel = ({ profileData }: { profileData?: StudentProfileData | null }) => {
  // 根据真实数据计算漏斗
  const getStages = () => {
    if (!profileData) {
      return [
        { name: '选校清单', count: 0, color: 'from-blue-400 to-blue-600', bg: 'bg-blue-50' },
        { name: '文书定稿', count: 0, color: 'from-indigo-400 to-indigo-600', bg: 'bg-indigo-50' },
        { name: '递交申请', count: 0, color: 'from-purple-400 to-purple-600', bg: 'bg-purple-50' },
        { name: '获得面试', count: 0, color: 'from-pink-400 to-pink-600', bg: 'bg-pink-50' },
        { name: '录取Offer', count: 0, color: 'from-emerald-400 to-emerald-600', bg: 'bg-emerald-50' }
      ]
    }

    const targetUniversities = profileData.targets?.targetUniversities?.length || 0
    const milestones = profileData.targets?.nextMilestones || []
    const completedMilestones = milestones.filter(m => m.completed).length
    const progress = profileData.progress?.overall || 0

    return [
      { name: '选校清单', count: targetUniversities, color: 'from-blue-400 to-blue-600', bg: 'bg-blue-50' },
      { name: '文书定稿', count: Math.max(0, Math.floor(targetUniversities * 0.7)), color: 'from-indigo-400 to-indigo-600', bg: 'bg-indigo-50' },
      { name: '递交申请', count: Math.max(0, Math.floor(targetUniversities * (progress / 200))), color: 'from-purple-400 to-purple-600', bg: 'bg-purple-50' },
      { name: '获得面试', count: completedMilestones, color: 'from-pink-400 to-pink-600', bg: 'bg-pink-50' },
      { name: '录取Offer', count: progress >= 80 ? 1 : 0, color: 'from-emerald-400 to-emerald-600', bg: 'bg-emerald-50' }
    ]
  }

  const stages = getStages()

  return (
    <div className="space-y-3 py-4">
      {stages.map((stage, index) => (
        <div key={index} className="flex items-center justify-center group">
          <div
            className={`relative h-12 flex items-center justify-between px-5 rounded-xl ${stage.bg} border border-transparent hover:border-slate-200 transition-all duration-300 cursor-pointer hover:shadow-md`}
            style={{ width: `${100 - index * 12}%` }}
          >
            <div
              className={`absolute inset-0 rounded-xl bg-gradient-to-r ${stage.color} opacity-0 group-hover:opacity-10 transition-opacity`}
            />
            <span className="text-sm font-semibold text-slate-700">{stage.name}</span>
            <div className="flex items-center gap-2">
              <span className="text-lg font-extrabold text-slate-800">{stage.count}</span>
              <span className="text-xs text-slate-400">所</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// 个人信息卡片项
const ProfileInfoItem = ({
  icon: Icon,
  label,
  value,
  editable = false
}: {
  icon: React.ElementType
  label: string
  value: string
  editable?: boolean
}) => (
  <div className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50/80 transition-colors group">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
      <Icon className="w-5 h-5 text-indigo-500" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-xs text-slate-400 mb-0.5">{label}</div>
      <div className="text-sm font-semibold text-slate-800 truncate">{value || '未设置'}</div>
    </div>
    {editable && (
      <PencilIcon className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 transition-colors" />
    )}
  </div>
)

// 档案数据卡片
const ProfileDataCard = ({ label, value, icon }: { label: string; value: string; icon?: React.ElementType }) => (
  <div className="p-4 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-100 hover:border-indigo-200 hover:shadow-sm transition-all group">
    <div className="flex items-start justify-between mb-2">
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</p>
      {icon && (
        <div className="w-6 h-6 rounded-lg bg-indigo-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          {/* @ts-ignore */}
          <icon className="w-3.5 h-3.5 text-indigo-500" />
        </div>
      )}
    </div>
    <p className="text-base font-bold text-slate-800">{value}</p>
  </div>
)

// 提升建议卡片
const SuggestionCard = ({
  icon: Icon,
  title,
  description,
  color
}: {
  icon: React.ElementType
  title: string
  description: string
  color: 'yellow' | 'blue' | 'purple' | 'green'
}) => {
  const colorClasses = {
    yellow: 'bg-amber-50 border-amber-100 text-amber-800',
    blue: 'bg-blue-50 border-blue-100 text-blue-800',
    purple: 'bg-purple-50 border-purple-100 text-purple-800',
    green: 'bg-emerald-50 border-emerald-100 text-emerald-800'
  }

  const iconColors = {
    yellow: 'text-amber-600',
    blue: 'text-blue-600',
    purple: 'text-purple-600',
    green: 'text-emerald-600'
  }

  return (
    <div className={`flex gap-3 p-4 rounded-xl border ${colorClasses[color]} transition-all hover:shadow-sm`}>
      <div className="mt-0.5 flex-shrink-0">
        <Icon className={`w-5 h-5 ${iconColors[color]}`} />
      </div>
      <div>
        <p className="text-sm font-bold mb-1">{title}</p>
        <p className="text-xs opacity-80 leading-relaxed">{description}</p>
      </div>
    </div>
  )
}

interface StudentProfileData {
  basic: {
    id: string
    name: string
    email?: string
    phone?: string
    avatar?: string
    createdAt: string
  }
  targets: {
    targetCountry?: string
    targetUniversities: string[]
    currentPhase?: string
    nextMilestones: Array<{
      title: string
      dueDate: string
      priority: string
      completed: boolean
    }>
  }
  languageScores: Record<string, {
    targetScore: number
    currentScore: number
    testDate?: string
  }>
  preferences: {
    subjects?: string[]
    learningStyle?: string
    priceRange?: { min: number; max: number }
    preferredTimeSlots?: string[]
    interests?: string[]
  }
  progress: {
    overall: number
    completedTasks: number
    totalTasks: number
    lastUpdated?: string
  }
}

const StudentProfile = () => {
  const { user, updateUser } = useAuthStore()
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [profileData, setProfileData] = useState<StudentProfileData | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    avatar: user?.avatar || ''
  })

  useEffect(() => {
    fetchProfileData()
  }, [])

  const fetchProfileData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch('/api/students/profile', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setProfileData(data)
      }
    } catch (error) {
      console.error('获取学生资料失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const ensureAbsoluteAvatarUrl = (avatarUrl: string | undefined): string => {
    if (!avatarUrl) return ''
    if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
      return avatarUrl
    }
    return `${window.location.origin}${avatarUrl}`
  }

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const validation = AvatarHelper.validateAvatarFile(file)
    if (!validation.valid) {
      alert(validation.message)
      return
    }

    setUploadingAvatar(true)
    try {
      const formDataObj = new FormData()
      formDataObj.append('avatar', file)
      const token = localStorage.getItem('token')
      const response = await fetch('/api/users/upload-avatar', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataObj
      })

      if (response.ok) {
        const data = await response.json()
        const fullAvatarUrl = `${window.location.origin}${data.avatarUrl}`
        const updatedUser = { ...user, avatar: fullAvatarUrl }
        updateUser(updatedUser)
        setFormData((prev) => ({ ...prev, avatar: fullAvatarUrl }))
        alert('头像上传成功！')
      } else {
        alert('上传头像失败')
      }
    } catch (error) {
      console.error('上传头像失败:', error)
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        const updatedUser = await response.json()
        updateUser(updatedUser)
        setIsEditing(false)
      } else {
        alert('更新个人资料失败')
      }
    } catch (error) {
      console.error('更新个人资料失败:', error)
    } finally {
      setSaving(false)
    }
  }

  const completionScore = useMemo(() => {
    const fields = [formData.firstName, formData.lastName, formData.email, formData.phone]
    const filled = fields.filter((f) => Boolean(f && f.trim())).length
    return Math.min(100, Math.round((filled / fields.length) * 100))
  }, [formData])

  const profileMetrics = useMemo(() => {
    return [
      {
        label: '资料完成度',
        value: `${completionScore}%`,
        helper: completionScore >= 80 ? '满足申请要求' : '建议补充信息',
        trend: completionScore >= 80 ? '良好' : '待完善',
        trendColor: (completionScore >= 80 ? 'positive' : 'neutral') as 'positive' | 'neutral'
      },
      {
        label: 'AI 建议',
        value: '2 条',
        helper: '提升科研/竞赛背景'
      },
      {
        label: '最近更新',
        value: new Date().toLocaleDateString('zh-CN'),
        helper: '档案更新时间'
      },
      {
        label: '综合竞争力',
        value: '强',
        helper: 'AI 综合评估'
      }
    ]
  }, [completionScore])

  if (loading) {
    return (
      <StudentPageLayout
        title="个人档案"
        description="完善个人信息、留学偏好与能力模型，AI 会及时反馈进一步建议。"
        metrics={profileMetrics}
      >
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-indigo-100 border-t-indigo-500 animate-spin" />
            <SparklesIcon className="w-6 h-6 text-indigo-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-slate-500 text-sm">正在加载个人档案...</p>
        </div>
      </StudentPageLayout>
    )
  }

  return (
    <StudentPageLayout
      title="个人档案"
      description="所有基础信息、目标院校与 AI 能力分析都会集中在此，帮助你随时把握申请状态。"
      metrics={profileMetrics}
      actions={
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl sm:rounded-full bg-white/15 backdrop-blur-sm text-white text-sm font-medium hover:bg-white/25 transition border border-white/20 w-full sm:w-auto"
        >
          <PencilIcon className="w-4 h-4" />
          {isEditing ? '取消编辑' : '编辑资料'}
        </button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* 左侧：个人名片 */}
        <div className="space-y-4 sm:space-y-6">
          {/* 个人信息卡片 */}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            {/* 头部背景 */}
            <div className="h-28 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 relative overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYtMi42ODYgNi02cy0yLjY4Ni02LTYtNi02IDIuNjg2LTYgNiAyLjY4NiA2IDYgNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10" />
              
              {/* 编辑按钮 */}
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="absolute top-3 right-3 p-2 bg-white/20 backdrop-blur-sm rounded-xl text-white hover:bg-white/30 transition-all hover:scale-105"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* 头像和基本信息 */}
            <div className="px-6 pb-6 relative">
              <div className="relative -mt-14 mb-4 inline-block">
                <div className="p-1.5 bg-white rounded-full shadow-lg">
                  <UserAvatar
                    avatar={ensureAbsoluteAvatarUrl(user?.avatar ?? undefined)}
                    firstName={user?.firstName ?? undefined}
                    lastName={user?.lastName ?? undefined}
                    size="xl"
                    className="w-24 h-24 text-2xl ring-4 ring-white"
                  />
                </div>
                {isEditing && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="absolute bottom-1 right-1 bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-2.5 rounded-full shadow-lg transition-all hover:scale-110 hover:shadow-xl"
                  >
                    {uploadingAvatar ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <CameraIcon className="w-4 h-4" />
                    )}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
              </div>

              {isEditing ? (
                <div className="space-y-4 mt-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">姓</label>
                      <input
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all"
                        placeholder="请输入姓"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">名</label>
                      <input
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all"
                        placeholder="请输入名"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">电话</label>
                    <input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all"
                      placeholder="请输入手机号"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setIsEditing(false)}
                      className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-slate-600 text-sm font-semibold hover:bg-slate-50 transition"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-indigo-200/50 hover:shadow-xl disabled:opacity-50 transition-all"
                    >
                      {saving ? '保存中...' : '保存更改'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl font-bold text-slate-900">
                      {user?.firstName} {user?.lastName}
                    </h2>
                    <CheckBadgeIcon className="w-5 h-5 text-indigo-500" />
                  </div>
                  <p className="text-sm text-slate-500 mb-5 flex items-center gap-1.5">
                    <SparklesSolid className="w-4 h-4 text-amber-400" />
                    学员 · 冲刺中
                  </p>

                  <div className="space-y-1 border-t border-slate-100 pt-4">
                    <ProfileInfoItem icon={EnvelopeIcon} label="邮箱" value={user?.email ?? ''} />
                    <ProfileInfoItem icon={PhoneIcon} label="电话" value={user?.phone ?? ''} editable />
                    <ProfileInfoItem
                      icon={CalendarIcon}
                      label="注册时间"
                      value={`${new Date(user?.createdAt || Date.now()).toLocaleDateString('zh-CN')}`}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 申请进度漏斗 */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <ArrowTrendingUpIcon className="w-5 h-5 text-indigo-500" />
                申请漏斗
              </h3>
              <span className="text-xs text-slate-400">实时追踪</span>
            </div>
            <ApplicationFunnel profileData={profileData} />
          </div>
        </div>

        {/* 右侧：能力模型与详细数据 */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* 六维能力模型 */}
          <StudentSection
            title="能力评估模型"
            description="基于您的学术成绩和背景活动分析"
            icon={StarIcon}
          >
            <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-100 overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-5 border-b border-slate-100">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200/50 flex-shrink-0">
                    <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm sm:text-base">综合竞争力分析</h4>
                    <p className="text-[10px] sm:text-xs text-slate-500">AI 多维度评估</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 self-start sm:self-auto">
                  <CheckCircleIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" />
                  <span className="text-xs sm:text-sm font-bold text-emerald-700">竞争力: 强</span>
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6">
                {/* 雷达图 - 移动端缩放 */}
                <div className="flex justify-center items-center bg-gradient-to-br from-slate-50/50 to-indigo-50/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 min-h-[250px] sm:min-h-[300px] overflow-hidden">
                  <div className="transform scale-[0.85] sm:scale-100 origin-center">
                    <AbilityRadar profileData={profileData} />
                  </div>
                </div>

                {/* 提升建议 */}
                <div className="space-y-3 sm:space-y-4">
                  <h4 className="font-bold text-slate-800 flex items-center gap-2 text-sm sm:text-base">
                    <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-500" />
                    AI 提升建议
                  </h4>
                  <div className="space-y-2 sm:space-y-3">
                    <SuggestionCard
                      icon={TrophyIcon}
                      title="竞赛经历薄弱"
                      description="建议参加 AMC12 或 Physics Bowl 提升背景竞争力，这将大幅增强您的申请材料亮点。"
                      color="yellow"
                    />
                    <SuggestionCard
                      icon={BeakerIcon}
                      title="科研项目机会"
                      description="您的 GPA 很优秀，可以尝试申请暑期科研项目，如 RSI 或大学实验室助研。"
                      color="blue"
                    />
                    <SuggestionCard
                      icon={GlobeAltIcon}
                      title="公益活动持续"
                      description="继续保持公益活动参与度，建议发起或深度参与一个长期项目。"
                      color="green"
                    />
                  </div>
                </div>
              </div>
            </div>
          </StudentSection>

          {/* 留学档案 */}
          <StudentSection
            title="留学档案"
            description="您的学术背景与申请目标"
            icon={AcademicCapIcon}
          >
            <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-100 p-4 sm:p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
                {[
                  { label: '目标国家', value: profileData?.targets?.targetCountry || '未设置', icon: GlobeAltIcon },
                  { label: '当前阶段', value: profileData?.targets?.currentPhase || '准备中', icon: AcademicCapIcon },
                  { label: '目标专业', value: profileData?.preferences?.subjects?.[0] || '未设置', icon: BeakerIcon },
                  { label: '完成进度', value: `${profileData?.progress?.overall || 0}%`, icon: StarIcon },
                  { label: '语言成绩', value: Object.entries(profileData?.languageScores || {}).map(([exam, score]) => `${exam}: ${score.currentScore}`).join(', ') || '未设置', icon: LanguageIcon },
                  { label: '完成任务', value: `${profileData?.progress?.completedTasks || 0}/${profileData?.progress?.totalTasks || 0}`, icon: DocumentTextIcon }
                ].map((item, i) => (
                  <ProfileDataCard key={i} label={item.label} value={item.value} icon={item.icon} />
                ))}
              </div>

              {/* 目标院校 */}
              <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <h4 className="font-bold text-indigo-900 flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base">
                    <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    梦校清单
                  </h4>
                  <button className="text-[10px] sm:text-xs text-indigo-600 font-semibold hover:underline">
                    编辑清单
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {(profileData?.targets?.targetUniversities?.length ? profileData.targets.targetUniversities : ['暂无目标院校']).map((school) => (
                    <span
                      key={school}
                      className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white rounded-lg text-xs sm:text-sm font-medium text-indigo-700 border border-indigo-200 shadow-sm"
                    >
                      {school}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </StudentSection>
        </div>
      </div>
    </StudentPageLayout>
  )
}

export default StudentProfile
